import { useEffect } from 'react';
import PropTypes from 'prop-types';

import { alpha, styled } from '@mui/material/styles';
import TreeItem, { treeItemClasses } from '@mui/lab/TreeItem';
import { TreeView } from '@mui/lab';
import { PromisePool } from '@supercharge/promise-pool';
import { Box, Skeleton, Collapse } from '@mui/material';
import { isNilOrBlank } from '../../utils/core';
import Iconify from '../Iconify';
// import { Scrollbar } from '../scrollbar';
import { useDispatch, useSelector } from '../../redux/store';
import {
  setActiveLeaf,
  setCurrentEntityId,
  updateFrontEndChart,
  setExpandedPyramidNodes,
  setSelectedPyramidNodes
} from '../../redux/slices/frontend';

// ----------------------------------------------------------------------

FrontEndMainPyramidSelector.propTypes = {
  hasPadding: PropTypes.bool,
  textColor: PropTypes.string,
  hasBgColor: PropTypes.bool
};

function TransitionComponent(props) {
  return <Collapse {...props} />;
}

TransitionComponent.propTypes = {
  /**
   * Show the component; triggers the enter or exit states
   */
  in: PropTypes.bool
};

const StyledTreeItem = styled((props) => <TreeItem {...props} TransitionComponent={TransitionComponent} />)(
  ({ theme }) => ({
    [`& .${treeItemClasses.content}`]: {
      borderTopRightRadius: theme.spacing(2),
      borderBottomRightRadius: theme.spacing(2),
      // paddingRight: theme.spacing(1),
      '&.Mui-expanded': {
        fontWeight: theme.typography.fontWeightBold,
        backgroundColor: `var(--tree-view-bg-color, ${theme.palette.action.selected})`,
        color: 'var(--tree-view-color)'
      }
    },
    [`& .${treeItemClasses.iconContainer}`]: {
      '& .close': {
        opacity: 0.3
      }
    }
  })
);

/* eslint-disable */

const renderTree = (nodes) =>
  nodes.map((node) => {
    const childrenNodes =
      node.children !== undefined && Array.isArray(node.children) && node.children.length > 0
        ? renderTree(node.children)
        : [<div key={`empty-${nodes.length}`} />];
    const nodeId = node.parent_id !== null ? String(node.parent_id + '-' + node.value) : String(node.value);

    return (
      <StyledTreeItem
        key={node.value}
        nodeId={nodeId}
        label={node.label}
        // disabled={mapIsFetching}
        collapseIcon={
          node.leaf ? (
            <Iconify icon="healthicons:ambulatory-clinic" color="primary.main" size={28} />
          ) : node.leaf === false && node.value === 1 ? (
            <Iconify icon="eva:globe-2-fill" color="primary.main" size={28} />
          ) : (
            <Iconify icon="eva:pin-outline" color="primary.main" size={28} />
          )
        }
        expandIcon={
          node.leaf ? (
            <Iconify icon="healthicons:ambulatory-clinic-outline" color="default.light" size={28} />
          ) : (
            <Iconify icon="eva:pin-outline" color="default.light" size={28} />
          )
        }
      >
        {childrenNodes}
      </StyledTreeItem>
    );
  });

/* eslint-enable */

export default function FrontEndMainPyramidSelector({ hasBgColor, textColor, hasPadding }) {
  const dispatch = useDispatch();
  const { widgets, pyramid, selectedPyramidNodes, currentPeriod, currentEntity, currentEntityId } = useSelector(
    (state) => state.frontend
  );

  const handleNodeSelect = async (event, nodeId) => {
    const currentNode = nodeId.split('-');
    if (currentNode.length === 1) {
      const jobs = [dispatch(setSelectedPyramidNodes(null)), dispatch(setCurrentEntityId(Number(currentNode[0])))];

      await PromisePool.for(jobs)
        .withConcurrency(4)
        .process(async (job) => job);
    }
  };

  const handleNodeToggle = async (event, nodesIds) => {
    if (nodesIds.length > 0) {
      const currentNode = nodesIds[0].split('-');
      if (Array.isArray(currentNode) && currentNode.length > 1) {
        const jobs = [
          dispatch(setExpandedPyramidNodes(nodesIds)),
          dispatch(setCurrentEntityId(Number(currentNode[1])))
        ];
        if (!isNilOrBlank(widgets) && widgets.length > 0) {
          widgets.forEach((widget) => {
            jobs.push(
              dispatch(
                updateFrontEndChart([
                  { value: true, field: 'isLoading', itemId: widget.id },
                  { value: Number(currentNode[1]), field: 'entity_id', itemId: widget.id },
                  { value: true, field: 'touched', itemId: widget.id }
                ])
              )
            );
          });
        }

        await PromisePool.for(jobs)
          .withConcurrency(4)
          .process(async (job) => job);
      }
    }
  };

  useEffect(() => {
    if (!isNilOrBlank(currentEntity) && currentEntity.status === 'active_leaf') {
      dispatch(setActiveLeaf(currentEntity));
    } else {
      dispatch(setActiveLeaf(null));
    }
  }, [currentEntity]); // eslint-disable-line

  return (
    <Box
      sx={{
        // maxHeight: '85vh',
        // display: 'flex',
        borderRadius: 1,
        alignItems: 'top',
        p: hasPadding === true ? 3 : undefined,
        // paddingTop: 2,
        // paddingRight: 3,
        // paddingBottom: 3,
        // paddingLeft: 3,
        backgroundColor: hasBgColor === true ? (theme) => alpha(theme.palette.grey[200], 0.95) : undefined,
        color: textColor
      }}
    >
      {isNilOrBlank(pyramid) ||
      isNilOrBlank(currentPeriod) ||
      isNilOrBlank(currentPeriod.start) ||
      isNilOrBlank(currentPeriod.start.label) ? (
        <>
          <Skeleton animation="wave" height={40} width="100%" />
          {[...Array(10)].map((_, si) => (
            <Skeleton key={`pyramid-skeleton-${si}`} animation="wave" height={30} width="100%" />
          ))}
        </>
      ) : (
        <TreeView
          aria-label="rich pyramid browser"
          onNodeToggle={handleNodeToggle}
          onNodeSelect={handleNodeSelect}
          expanded={selectedPyramidNodes === null ? [String(currentEntityId)] : selectedPyramidNodes}
          defaultCollapseIcon={<Iconify icon="eva:globe-2-fill" color="primary.main" size={28} />}
          defaultExpandIcon={<Iconify icon="eva:globe-2-outline" color="default.light" size={28} />}
          sx={{ flexGrow: 1, overflowY: 'auto' }}
        >
          {renderTree(pyramid.tree)}
        </TreeView>
      )}
    </Box>
  );
}
